interface Box {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/** What a map shows: `scale` px per canvas unit, with the canvas point `center` in the middle of its box. */
export interface MapCamera {
  readonly scale: number;
  readonly centerX: number;
  readonly centerY: number;
}

/** One end of the overview's growth: the map's box, its corner radius and what it shows there. */
export interface MaximapEnd {
  readonly box: Box;
  readonly radius: number;
  readonly camera: MapCamera;
}

export interface MaximapMorphFrame extends MaximapEnd {
  /** 0 = the small map's plain picture, 1 = the overview with its sectors and coordinates. */
  readonly detail: number;
}

const mix = (from: number, to: number, share: number) =>
  from + (to - from) * share;

/**
 * The overview at `progress` between the small map (0) and its full size (1). The box grows while
 * the camera zooms from what the small map shows to the whole canvas, so the picture is one map all
 * the way: it starts as the small map's picture, in the small map's place, and returns to it.
 */
export function maximapMorphFrame(
  small: MaximapEnd,
  full: MaximapEnd,
  progress: number
): MaximapMorphFrame {
  const p = Math.min(1, Math.max(0, progress));
  const from = small.camera.scale;
  const to = full.camera.scale;
  const zooms = from > 0 && to > 0 && from !== to;
  // Zoom at an even rate; the centre travels with the stretch of canvas the map shows, so one
  // place of the canvas keeps its spot in the map while everything around it comes into view.
  const scale = zooms ? from * Math.pow(to / from, p) : mix(from, to, p);
  const travelled = zooms ? (1 / scale - 1 / from) / (1 / to - 1 / from) : p;
  return {
    box: {
      left: mix(small.box.left, full.box.left, p),
      top: mix(small.box.top, full.box.top, p),
      width: mix(small.box.width, full.box.width, p),
      height: mix(small.box.height, full.box.height, p),
    },
    radius: mix(small.radius, full.radius, p),
    camera: {
      scale,
      centerX: mix(small.camera.centerX, full.camera.centerX, travelled),
      centerY: mix(small.camera.centerY, full.camera.centerY, travelled),
    },
    detail: p,
  };
}

/** Set on the small map while the overview stands in for it. */
export const MAXIMAP_OPEN_ATTRIBUTE = 'data-maximap-open';

/**
 * End the overview: the small map shows again in the very task that closes the dialog. The dialog's
 * `close` event only comes a task later; waiting for it paints a frame with neither map.
 */
export function handOverToSmallMap(
  smallMap: Pick<Element, 'toggleAttribute'>,
  dialog: Pick<HTMLDialogElement, 'close'>
) {
  smallMap.toggleAttribute(MAXIMAP_OPEN_ATTRIBUTE, false);
  dialog.close();
}
